import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from './services/ApiService';
import { AddinLogin } from './components/Login/AddinLogin';
import { ChatPanel } from './components/Chat/ChatPanel';
import { DiscoveryQuestion } from './components/Discovery/DiscoveryQuestion';
import { NoAccess } from './components/Access/NoAccess';
import { useAccessPolling } from './hooks/useAccessPolling';
import { applyFormulaRules } from './services/excel/legacy';
import type { AccessReason } from './types/agent';

// B8 self-test (dev only): `index.html?selftest=1` runs every tool against a scratch sheet.
// Resolved through `import.meta.glob` so a checkout without `src/taskpane/selftest/` (the harness
// is optional) still type-checks and builds. Only a dev build carries it: `import.meta.env.DEV` is false in
// `vite build`, the glob and its chunk are dropped from the bundle (the build job checks `dist/`), and
// `?selftest=1` on the production host shows the chat. It ran 180 writes without approval, switched the
// workbook to manual calculation and posted a capture, with no session (security audit 2026-10-13, AUD-22).
const loadSelfTest = import.meta.env.DEV
  ? import.meta.glob<{ default: React.ComponentType }>('./selftest/SelfTestView.tsx')['./selftest/SelfTestView.tsx']
  : undefined;
const SelfTestView = loadSelfTest ? lazy(loadSelfTest) : null;
const isSelfTest = () => import.meta.env.DEV && new URLSearchParams(window.location.search).get('selftest') === '1';

/** Taskpane state machine (PLAN-agent-v2 §5.2): loading → login | discovery | no-access | chat. Office.onReady()
 *  has resolved before App mounts (see index.tsx). `discovery` = the optional question of the first connection, once,
 *  before the rest. `no-access` = signed in but the agent gate is closed (GET /agent-v2/access: an unverified email, a
 *  suspended organization), also reached when a gated call later answers 403. */
type Phase = 'loading' | 'login' | 'discovery' | 'no-access' | 'chat';

/** The server's formula rules into the add-in's lint (sculpt-api docs/FORMULA_RULES.md, P2), so both refuse the
 *  same formulas. A backend that does not serve them, or an error, keeps the built-in rules. */
async function loadFormulaRules(): Promise<void> {
  try {
    applyFormulaRules(await apiService.getFormulaRules());
  } catch {
    // keep the built-in rules
  }
}

export default function App() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  // Owners and admins see the portal's billing pages in the account menu and "Upgrade" on the usage bar.
  const [admin, setAdmin] = useState(false);
  const [reason, setReason] = useState<AccessReason | null>(null);
  const inExcel = typeof Office !== 'undefined' && typeof Excel !== 'undefined';
  const selfTest = isSelfTest();

  /** Entitlement check after a valid session: true when the chat may open. */
  const checkAccess = useCallback(async (): Promise<boolean> => {
    try {
      const access = await apiService.getAccess();
      setReason(access.enabled ? null : (access.reason ?? null));
      if (access.enabled) await loadFormulaRules();
      setPhase(access.enabled ? 'chat' : 'no-access');
      return access.enabled;
    } catch {
      // The access route itself failing (network, 5xx) must not lock the user out of a session
      // that is otherwise valid: fall back to the chat, whose gated calls will re-check.
      setPhase('chat');
      return true;
    }
  }, []);

  const resolvePhase = useCallback(async () => {
    try {
      const user = await apiService.getCurrentUser();
      setEmail(user.email ?? null);
      setFirstName(user.first_name?.trim() || null);
      setAdmin(user.organization?.role === 'owner' || user.organization?.role === 'admin');
      if (user.discovery_asked === false) {
        setPhase('discovery');
        return;
      }
    } catch {
      setPhase('login');
      return;
    }
    await checkAccess();
  }, [checkAccess]);

  useEffect(() => {
    if (selfTest) return;
    void resolvePhase();
  }, [resolvePhase, selfTest]);

  // A 403 `agent_v2_access_required` on any later /agent-v2 call (the organization suspended mid-session, say):
  // re-read the access so the screen knows the reason, falling back to the generic copy when that fails.
  useEffect(
    () =>
      apiService.onAccessDenied(() => {
        setPhase('no-access');
        void checkAccess();
      }),
    [checkAccess],
  );

  // A 401 on any later call (the session expired or was revoked, the refresh refused): back to the login.
  useEffect(
    () =>
      apiService.onUnauthorized(() => {
        setEmail(null);
        setFirstName(null);
        setPhase('login');
      }),
    [],
  );

  // The gated screen watches for the gate to open (the mail link clicked, the organization restored).
  useAccessPolling(phase === 'no-access' && !selfTest, reason, checkAccess);

  const signOut = async () => {
    await apiService.logout();
    setEmail(null);
    setFirstName(null);
    setPhase('login');
  };

  if (!inExcel) {
    return <p className="p-6 text-center text-sm text-slate-500">{t('app.notExcel')}</p>;
  }
  if (selfTest) {
    if (!SelfTestView) return <p className="p-6 text-center text-sm text-slate-500">{t('app.selfTestMissing')}</p>;
    return (
      <Suspense fallback={<p className="p-6 text-center text-sm text-slate-400">{t('app.loading')}</p>}>
        <SelfTestView />
      </Suspense>
    );
  }
  if (phase === 'loading') return <p className="p-6 text-center text-sm text-slate-400">{t('app.loading')}</p>;
  if (phase === 'login') return <AddinLogin onAuthenticated={() => void resolvePhase()} />;
  if (phase === 'discovery') return <DiscoveryQuestion onDone={() => void checkAccess()} />;
  if (phase === 'no-access') return <NoAccess email={email} reason={reason} onCheckAgain={checkAccess} onResend={() => apiService.resendVerification()} onSignOut={() => void signOut()} />;
  return <ChatPanel firstName={firstName} email={email} admin={admin} onSignedOut={() => setPhase('login')} />;
}
